import { z } from 'zod';
import { getErrorMessage as errorMessage } from '@/utils/errorMessage';
import { DocumentService, type DocumentMetadata } from './documentService';
import { supabase } from '@/utils/supabase';
import { CATEGORIES_WITH_SUBTYPES, EFFECTIVE_DATE_CATEGORIES, type InvestorDocument } from '@/types/investor';

export type UploadChannel = 'wholesale' | 'correspondent';
export interface UploadFields {
  category: string;
  channel: UploadChannel | '';
  subType: string;
  effectiveDate: string;
  notes: string;
  hiddenRoles: string[];
  linkedTabs: ('processing' | 'closing')[];
}
export const emptyUploadFields = (channel: UploadFields['channel']): UploadFields => ({
  category: '', channel, subType: '', effectiveDate: '', notes: '',
  hiddenRoles: [], linkedTabs: []
});
export const MAX_BATCH_FILES = 25;
export const MAX_BATCH_BYTES = 200 * 1024 * 1024;

const fieldsSchema = z.object({
  category: z.string().trim().min(1, 'Choose a document type.'),
  channel: z.enum(['wholesale', 'correspondent'], { errorMap: () => ({ message: 'Choose a channel.' }) }),
  subType: z.string(), effectiveDate: z.string(), notes: z.string(),
  hiddenRoles: z.array(z.string()), linkedTabs: z.array(z.enum(['processing', 'closing']))
});

export function validateUploadFields(fields: UploadFields, categories: string[], channels: UploadChannel[]): string | null {
  const parsed = fieldsSchema.safeParse(fields);
  if (!parsed.success) {
    return parsed.error.issues[0].message;
  }
  if (!categories.includes(fields.category)) {
    return 'Choose an available document type.';
  }
  if (!channels.includes(parsed.data.channel)) {
    return 'Choose an available channel.';
  }
  if (CATEGORIES_WITH_SUBTYPES.includes(fields.category) && !fields.subType.trim()) {
    return `${fields.category} requires a descriptive name (sub-type)`;
  }
  if (fields.effectiveDate && EFFECTIVE_DATE_CATEGORIES.includes(fields.category)) {
    const date = new Date(`${fields.effectiveDate}T00:00:00Z`);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(fields.effectiveDate) || Number.isNaN(date.getTime()) ||
      date.toISOString().slice(0, 10) !== fields.effectiveDate) {
      return 'Enter a valid effective date.';
    }
  }
  return null;
}

export type BatchStatus = 'draft' | 'checking' | 'ready' | 'duplicate' | 'uploading' |
  'saving' | 'complete' | 'failed' | 'stopped' | 'skipped';
export interface InvestorUploadRow {
  id: string;
  file: File;
  fields: UploadFields;
  status: BatchStatus;
  progress: number;
  hash?: string;
  checked: boolean;
  duplicate?: string;
  duplicateChoice?: 'separate' | 'skip';
  error?: string;
  checkpoint: {
    metadata?: DocumentMetadata;
    storage?: { url: string; path: string };
    document?: InvestorDocument;
  };
}

export function createUploadRow(file: File, channel: UploadFields['channel']): InvestorUploadRow {
  return {
    id: crypto.randomUUID(), file, fields: emptyUploadFields(channel),
    status: 'draft', progress: 0, checked: false, checkpoint: {}
  };
}

export function validateBatchLimits(files: Pick<File, 'size'>[]): string | null {
  if (!files.length) {
    return 'Choose at least one file.';
  }
  if (files.length > MAX_BATCH_FILES) {
    return 'Choose no more than 25 files per batch.';
  }
  if (files.reduce((sum, file) => sum + file.size, 0) > MAX_BATCH_BYTES) {
    return 'The batch exceeds 200 MB. Remove files before continuing.';
  }
  return null;
}

const identityKey = (hash: string | undefined, category: string, channel: string, subType = '') => [
  hash, category, channel, category === 'Credit Guidelines' ? subType.trim() : ''
].join('::');
const duplicateKey = (row: InvestorUploadRow) =>
  identityKey(row.hash, row.fields.category, row.fields.channel, row.fields.subType);

/** In-memory session. Each row keeps its completed steps across retries. */
export class InvestorUploadBatch {
  private running = false;
  private stopped = false;
  constructor(
    readonly investorId: string,
    readonly organizationId: string,
    readonly rows: InvestorUploadRow[],
    private readonly changed: () => void
  ) { }

  stop() {
    this.stopped = true;
  }

  async prepare(categories: string[], channels: UploadChannel[]): Promise<void> {
    if (this.running) {
      return;
    }
    this.running = true;
    this.stopped = false;
    try {
      const limit = validateBatchLimits(this.rows.map(row => row.file));
      if (limit) {
        throw new Error(limit);
      }
      // Hash one file at a time. Validation and duplicate lookup never write.
      // Started files may have no saved record yet, so seed their frozen identity first.
      const seen = new Map<string, string>();
      for (const row of this.rows) {
        const frozen = row.checkpoint.metadata;
        if (frozen) {
          seen.set(identityKey(frozen.fileHash, frozen.category, frozen.documentType, frozen.subType), row.file.name);
        }
      }
      for (const row of this.rows) {
        if (this.stopped) {
          break;
        }
        if (row.checkpoint.metadata) {
          continue;
        }
        row.error = DocumentService.validateFile(row.file) ||
          validateUploadFields(row.fields, categories, channels) || undefined;
        row.checked = false;
        if (row.error) {
          row.status = 'draft'; this.changed(); continue;
        }
        row.status = 'checking';
        this.changed();
        try {
          row.hash ??= await DocumentService.computeFileHash(row.file);
          const duplicate = await DocumentService.checkForDuplicate(
            this.investorId, row.hash, row.fields.category, row.fields.channel as UploadChannel,
            row.fields.category === 'Credit Guidelines' ? row.fields.subType.trim() : undefined
          );
          const key = duplicateKey(row);
          row.duplicate = duplicate?.originalFileName ?? seen.get(key);
          seen.set(key, row.file.name);
          row.checked = true;
          row.status = row.duplicateChoice === 'skip' ? 'skipped' :
            row.duplicate && row.duplicateChoice !== 'separate' ? 'duplicate' : 'ready';
        } catch (error) {
          row.error = errorMessage(error);
          row.status = 'draft';
        }
        this.changed();
      }
    } finally {
      this.running = false; this.changed();
    }
  }

  async run(categories: string[], channels: UploadChannel[]): Promise<void> {
    if (this.running) {
      return;
    }
    const limit = validateBatchLimits(this.rows.map(row => row.file));
    if (limit) {
      throw new Error(limit);
    }
    const included = this.rows.filter(row => row.status !== 'skipped' && row.status !== 'complete');
    if (!included.length) {
      return;
    }
    for (const row of included) {
      const error = DocumentService.validateFile(row.file) || validateUploadFields(row.fields, categories, channels);
      if (error || !row.checked || (row.duplicate && row.duplicateChoice !== 'separate')) {
        throw new Error(error || 'Review every file and resolve duplicates before uploading.');
      }
    }
    this.running = true;
    this.stopped = false;
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error || !user) {
        throw new Error('Sign in again before uploading.');
      }
      const process = async (row: InvestorUploadRow) => {
        if (this.stopped) {
          row.status = 'stopped'; this.changed(); return;
        }
        row.error = undefined;
        const fields = row.fields;
        try {
          row.checkpoint.metadata ??= {
            investorId: this.investorId, organizationId: this.organizationId,
            documentType: fields.channel as UploadChannel, category: fields.category,
            originalFileName: row.file.name, fileSize: row.file.size, mimeType: row.file.type,
            uploadedBy: user.id, notes: fields.notes.trim() || undefined,
            subType: fields.subType.trim() || undefined, fileHash: row.hash, version: 1,
            effectiveDate: EFFECTIVE_DATE_CATEGORIES.includes(fields.category) ? fields.effectiveDate || undefined : undefined,
            hiddenRoles: [...fields.hiddenRoles], linkedTabs: [...fields.linkedTabs]
          };
          if (!row.checkpoint.storage) {
            row.status = 'uploading'; this.changed();
            row.checkpoint.storage = await DocumentService.uploadDocument(row.file, row.checkpoint.metadata, {
              documentId: row.id, onProgress: progress => {
                row.progress = progress; this.changed();
              }
            });
          }
          if (!row.checkpoint.document) {
            row.status = 'saving'; this.changed();
            row.checkpoint.document = await DocumentService.saveDocumentMetadata(
              row.checkpoint.metadata, row.checkpoint.storage.url, row.checkpoint.storage.path, 1, row.id
            );
            this.changed();
          }
          row.status = 'complete';
        } catch (error) {
          row.status = 'failed';
          row.error = errorMessage(error);
        }
        this.changed();
      };
      // At most three files transfer at once; each worker takes the next unstarted row.
      let index = 0;
      await Promise.all(Array.from({ length: Math.min(3, included.length) }, async () => {
        while (index < included.length) {
          await process(included[index++]);
        }
      }));
    } finally {
      this.running = false; this.changed();
    }
  }
}
